import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, Tooltip, Cell } from "recharts";
import { LogOut, Download, Users, AlertTriangle, MapPinned, Mail, Search, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, API } from "@/lib/api";
import { WARDS } from "@/lib/constants";

const STATUS_STYLES = {
  new: "bg-cyan-50 text-cyan-700 border-cyan-200",
  reviewing: "bg-amber-50 text-amber-700 border-amber-200",
  actioned: "bg-emerald-50 text-emerald-700 border-emerald-200",
  closed: "bg-slate-100 text-slate-500 border-slate-200",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [stats, setStats] = useState(null);
  const [responses, setResponses] = useState([]);
  const [wardFilter, setWardFilter] = useState("all");
  const [issueOnly, setIssueOnly] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (wardFilter !== "all") params.ward = wardFilter;
      if (issueOnly) params.has_issue = true;
      const [s, r] = await Promise.all([api.get("/admin/stats"), api.get("/admin/responses", { params })]);
      setStats(s.data);
      setResponses(r.data);
    } catch (e) {
      toast.error("Could not load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wardFilter, issueOnly]);

  const filtered = useMemo(() => {
    if (!search.trim()) return responses;
    const q = search.toLowerCase();
    return responses.filter((r) =>
      [r.ward, r.top_priority, r.priority_comment, r.issue?.description, r.issue?.location, r.contact?.name, r.contact?.email]
        .filter(Boolean).join(" ").toLowerCase().includes(q)
    );
  }, [responses, search]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/responses/${id}/status`, { status });
      setResponses((prev) => prev.map((r) => (r.id === id ? { ...r, status } : r)));
      if (selected?.id === id) setSelected({ ...selected, status });
      toast.success("Status updated");
    } catch {
      toast.error("Could not update status");
    }
  };

  const exportCsv = async () => {
    try {
      const token = localStorage.getItem("ts_token");
      const res = await fetch(`${API}/admin/export`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tellsolihull-responses.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success("Export downloaded");
    } catch {
      toast.error("Export failed");
    }
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
    catch { return iso; }
  };

  const statCards = [
    { label: "Total responses", value: stats?.total ?? "—", icon: Users, color: "text-cyan-600 bg-cyan-50" },
    { label: "Issues reported", value: stats?.issues ?? "—", icon: AlertTriangle, color: "text-amber-600 bg-amber-50" },
    { label: "Wards covered", value: stats ? `${stats.wards_covered}/17` : "—", icon: MapPinned, color: "text-emerald-600 bg-emerald-50" },
    { label: "Contact opt-ins", value: stats?.contacts ?? "—", icon: Mail, color: "text-indigo-600 bg-indigo-50" },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* top bar */}
      <header className="bg-[#0A192F] text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Logo light />
            <span className="hidden sm:inline-block h-6 w-px bg-white/15" />
            <span className="hidden sm:inline text-sm font-semibold text-slate-300">Responses dashboard</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-xs text-slate-400">{user?.email}</span>
            <button onClick={exportCsv} data-testid="export-csv" className="inline-flex items-center gap-2 rounded-full bg-[#12B6CF] hover:bg-[#0FA0B7] text-white text-sm font-semibold px-4 py-2 transition-colors">
              <Download size={15} /> Export CSV
            </button>
            <button onClick={() => { logout(); navigate("/admin"); }} data-testid="admin-logout" className="inline-flex items-center gap-2 rounded-full border border-white/15 hover:bg-white/10 text-sm font-semibold px-4 py-2 transition-colors">
              <LogOut size={15} /> <span className="hidden sm:inline">Sign out</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="stat-cards">
          {statCards.map((c) => (
            <div key={c.label} className="rounded-2xl bg-white border border-slate-200 p-5">
              <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${c.color}`}><c.icon size={20} /></div>
              <p className="mt-4 font-display font-extrabold text-3xl text-[#0F172A]">{c.value}</p>
              <p className="text-sm text-slate-500 mt-0.5">{c.label}</p>
            </div>
          ))}
        </div>

        {/* charts */}
        <div className="mt-6 grid lg:grid-cols-2 gap-6">
          <ChartCard title="Top priorities across the borough" data={(stats?.priority_breakdown || []).slice(0, 8)} xKey="priority" testid="chart-priorities" />
          <ChartCard title="Responses by ward" data={(stats?.ward_breakdown || []).slice(0, 8)} xKey="ward" testid="chart-wards" />
        </div>

        {/* filters */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3 sm:items-center justify-between">
          <h2 className="font-display font-bold text-xl text-[#0F172A]">Responses <span className="text-slate-400 font-sans font-medium text-base">({filtered.length})</span></h2>
          <div className="flex flex-wrap items-center gap-2.5">
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-input" placeholder="Search responses…" className="rounded-full border border-slate-200 bg-white pl-9 pr-4 py-2 text-sm w-52 focus:border-[#12B6CF] focus:ring-2 focus:ring-[#12B6CF]/20 outline-none" />
            </div>
            <select value={wardFilter} onChange={(e) => setWardFilter(e.target.value)} data-testid="ward-filter" className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 focus:border-[#12B6CF] outline-none">
              <option value="all">All wards</option>
              {WARDS.map((w) => <option key={w} value={w}>{w}</option>)}
            </select>
            <button onClick={() => setIssueOnly(!issueOnly)} data-testid="issue-filter" className={`rounded-full px-4 py-2 text-sm font-semibold border transition-colors ${issueOnly ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-700 border-slate-200 hover:border-slate-300"}`}>
              Issues only
            </button>
          </div>
        </div>

        {/* table */}
        <div className="mt-4 rounded-2xl bg-white border border-slate-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="responses-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-left text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Ward</th>
                  <th className="px-4 py-3">Top priority</th>
                  <th className="px-4 py-3">Issue</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400">Loading…</td></tr>
                ) : filtered.length === 0 ? (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-400" data-testid="empty-state">No responses yet.</td></tr>
                ) : filtered.map((r) => (
                  <tr key={r.id} onClick={() => setSelected(r)} className="hover:bg-slate-50 cursor-pointer transition-colors" data-testid={`response-row-${r.id}`}>
                    <td className="px-4 py-3 text-slate-500 whitespace-nowrap">{fmtDate(r.created_at)}</td>
                    <td className="px-4 py-3 font-semibold text-slate-800">{r.ward}</td>
                    <td className="px-4 py-3 text-slate-700">{r.top_priority || "—"}</td>
                    <td className="px-4 py-3">
                      {r.issue?.has_issue ? (
                        <span className="inline-flex items-center gap-1 text-amber-700 font-semibold"><AlertTriangle size={13} /> {r.issue.category}</span>
                      ) : <span className="text-slate-300">—</span>}
                    </td>
                    <td className="px-4 py-3 text-slate-600">{r.anonymous ? <span className="text-slate-400 italic">Anonymous</span> : (r.contact?.name || r.contact?.email || "—")}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-block rounded-full border px-2.5 py-0.5 text-xs font-semibold capitalize ${STATUS_STYLES[r.status] || STATUS_STYLES.new}`}>{r.status || "new"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>

      {/* detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="detail-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setSelected(null)} />
          <div className="relative w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl">
            <div className="sticky top-0 bg-[#0A192F] text-white px-6 py-4 flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-wider text-cyan-300 font-bold">{selected.ward}</p>
                <p className="font-display font-bold text-lg">Response detail</p>
              </div>
              <button onClick={() => setSelected(null)} data-testid="close-drawer" className="text-slate-300 hover:text-white"><X /></button>
            </div>
            <div className="p-6 space-y-5">
              <Field label="Submitted">{fmtDate(selected.created_at)}</Field>
              <Field label="Top priority">{selected.top_priority || "Not selected"}</Field>
              <Field label="All priorities">{selected.priorities?.join(", ") || "None"}</Field>
              {selected.council_satisfaction && <Field label="Council rating">{selected.council_satisfaction}</Field>}
              {selected.right_direction && <Field label="Right direction?">{selected.right_direction}</Field>}
              {selected.years_resident && <Field label="Years in Solihull">{selected.years_resident}</Field>}
              {selected.priority_comment && <Field label="Comment">{selected.priority_comment}</Field>}
              {selected.voting_intention && <Field label="Voting intention">{selected.voting_intention}</Field>}
              {selected.issue?.has_issue && (
                <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 space-y-2">
                  <p className="text-xs font-bold uppercase tracking-wider text-amber-700 flex items-center gap-1"><AlertTriangle size={13} /> Issue reported · {selected.issue.severity}</p>
                  <p className="text-sm font-semibold text-slate-800">{selected.issue.category}</p>
                  {selected.issue.location && <p className="text-sm text-slate-600">📍 {selected.issue.location}</p>}
                  <p className="text-sm text-slate-700">{selected.issue.description}</p>
                </div>
              )}
              <div className="rounded-xl border border-slate-200 p-4">
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400">Contact</p>
                {selected.anonymous ? (
                  <p className="mt-1 text-sm italic text-slate-400">Anonymous submission</p>
                ) : (
                  <div className="mt-1 text-sm text-slate-700 space-y-0.5">
                    <p>{selected.contact?.name || "—"}</p>
                    <p>{selected.contact?.email || "—"}</p>
                    <p>{selected.contact?.postcode || ""} {selected.contact?.phone || ""}</p>
                    {selected.contact?.opt_in && <p className="text-emerald-600 font-semibold">✓ Opted in to updates</p>}
                  </div>
                )}
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Update status</p>
                <div className="grid grid-cols-2 gap-2">
                  {["new", "reviewing", "actioned", "closed"].map((s) => (
                    <button key={s} onClick={() => setStatus(selected.id, s)} data-testid={`set-status-${s}`} className={`rounded-lg border px-3 py-2 text-sm font-semibold capitalize transition-colors ${selected.status === s ? "bg-[#0A192F] text-white border-[#0A192F]" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`}>{s}</button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ChartCard({ title, data, xKey, testid }) {
  return (
    <div className="rounded-2xl bg-white border border-slate-200 p-5" data-testid={testid}>
      <h3 className="font-display font-bold text-base text-[#0F172A] mb-4">{title}</h3>
      {data.length === 0 ? (
        <div className="h-56 flex items-center justify-center text-slate-400 text-sm">No data yet</div>
      ) : (
        <ResponsiveContainer width="100%" height={224}>
          <BarChart data={data} layout="vertical" margin={{ left: 8, right: 16 }}>
            <XAxis type="number" hide />
            <YAxis type="category" dataKey={xKey} width={130} tick={{ fontSize: 11, fill: "#475569" }} tickFormatter={(v) => (v.length > 20 ? v.slice(0, 18) + "…" : v)} />
            <Tooltip cursor={{ fill: "#f1f5f9" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0", fontSize: 13 }} />
            <Bar dataKey="count" radius={[0, 6, 6, 0]}>
              {data.map((_, i) => <Cell key={i} fill={i === 0 ? "#0A788A" : "#12B6CF"} />)}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</p>
      <p className="mt-1 text-sm font-semibold text-slate-800">{children}</p>
    </div>
  );
}
